import React from "react";
import { Fragment } from "react";

import { css } from "@emotion/core";
import { capitalize } from "lodash";

import { TextInput } from "@zapier/design-system";

import PaymentFormField from "@zapier/payment/esm/components/PaymentFormField";
import PostalCodeInput from "@zapier/payment/esm/components/PostalCodeInput";
import SensitiveData from "@zapier/payment/esm/components/SensitiveData";
import StateSelect from "@zapier/payment/esm/components/StateSelect";
import { useSize } from "@zapier/payment/esm/hooks/useSize";
import {
  BankAccountType,
  BankFormFields,
  BankOwnershipType,
} from "@zapier/payment/esm/types";
import {
  getValue,
  handleChange,
  hasError,
} from "@zapier/payment/esm/utils/formHelpers";

import RadioButtonGroup from "../RadioButtonGroup";

const accountTypeOptions = [
  {
    key: BankAccountType.checking,
    displayText: capitalize(BankAccountType.checking),
  },
  {
    key: BankAccountType.savings,
    displayText: capitalize(BankAccountType.savings),
  },
];
const ownershipTypeOptions = [
  {
    key: BankOwnershipType.business,
    displayText: capitalize(BankOwnershipType.business),
  },
  {
    key: BankOwnershipType.personal,
    displayText: capitalize(BankOwnershipType.personal),
  },
];

const rootStyle = css`
  display: grid;
  grid-gap: 10px 20px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
`;

/* Ensures the grid columns are equally sized */
const radioGroupCss = css`
  margin-left: 0.5rem;
  margin-top: 1rem;
`;

type Props = {
  fields: BankFormFields;
  onChangeField: (field: string, value: string) => void;
};

const BankAccountFormFields: React.FC<Props> = ({ fields, onChangeField }) => {
  const size = useSize();
  const isSmall = size === "small";
  const ownershipType = getValue("ownershipType", fields);
  return (
    <div css={rootStyle}>
      <PaymentFormField
        id="routingNumber"
        label="Routing Number"
        required={true}
        span={isSmall ? 4 : 2}
      >
        <SensitiveData>
          <TextInput
            // @ts-ignore - This component is housed in the packages/products package, so the missing `hasError` type will have to be fixed there
            hasError={hasError("routingNumber", fields)}
            maxLength={9}
            onChange={handleChange(onChangeField, "routingNumber", {
              ignoreWhitespace: true,
            })}
            placeholder="Routing number"
            type="tel"
            value={getValue("routingNumber", fields)}
          />
        </SensitiveData>
      </PaymentFormField>

      <PaymentFormField
        id="accountNumber"
        label="Account Number"
        required={true}
        span={isSmall ? 4 : 2}
      >
        <SensitiveData>
          <TextInput
            // @ts-ignore - This component is housed in the packages/products package, so the missing `hasError` type will have to be fixed there
            hasError={hasError("accountNumber", fields)}
            onChange={handleChange(onChangeField, "accountNumber", {
              ignoreWhitespace: true,
            })}
            placeholder="Account number"
            type="tel"
            value={getValue("accountNumber", fields)}
          />
        </SensitiveData>
      </PaymentFormField>

      <PaymentFormField
        id="accountType"
        isGroup={true}
        label="Account type"
        span={isSmall ? 4 : 2}
      >
        <div css={radioGroupCss}>
          <RadioButtonGroup
            id="accountType"
            onSelectOption={handleChange(onChangeField, "accountType")}
            options={accountTypeOptions}
            selectedOption={getValue("accountType", fields)}
          />
        </div>
      </PaymentFormField>

      <PaymentFormField
        id="ownershipType"
        isGroup={true}
        label="Ownership type"
        span={isSmall ? 4 : 2}
      >
        <div css={radioGroupCss}>
          <RadioButtonGroup
            id="ownershipType"
            onSelectOption={handleChange(onChangeField, "ownershipType")}
            options={ownershipTypeOptions}
            selectedOption={ownershipType}
          />
        </div>
      </PaymentFormField>

      {ownershipType === BankOwnershipType.personal && (
        <Fragment>
          <PaymentFormField id="firstName" label="First name" span={2}>
            <TextInput
              // @ts-ignore - This component is housed in the packages/products package, so the missing `hasError` type will have to be fixed there
              hasError={hasError("firstName", fields)}
              onChange={handleChange(onChangeField, "firstName")}
              placeholder="First name"
              required={true}
              value={getValue("firstName", fields)}
            />
          </PaymentFormField>
          <PaymentFormField id="lastName" label="Last name" span={2}>
            <TextInput
              // @ts-ignore - This component is housed in the packages/products package, so the missing `hasError` type will have to be fixed there
              hasError={hasError("lastName", fields)}
              onChange={handleChange(onChangeField, "lastName")}
              placeholder="Last name"
              required={true}
              value={getValue("lastName", fields)}
            />
          </PaymentFormField>
        </Fragment>
      )}

      {ownershipType === BankOwnershipType.business && (
        <PaymentFormField
          id="businessName"
          label="Business name"
          required={true}
          span={4}
        >
          <TextInput
            // @ts-ignore - This component is housed in the packages/products package, so the missing `hasError` type will have to be fixed there
            hasError={hasError("businessName", fields)}
            onChange={handleChange(onChangeField, "businessName")}
            placeholder="Business name"
            value={getValue("businessName", fields)}
          />
        </PaymentFormField>
      )}

      <PaymentFormField
        id="streetAddress"
        label="Street address"
        required={true}
        span={4}
      >
        <SensitiveData>
          <TextInput
            // @ts-ignore - This component is housed in the packages/products package, so the missing `hasError` type will have to be fixed there
            hasError={hasError("streetAddress", fields)}
            onChange={handleChange(onChangeField, "streetAddress")}
            placeholder="Street and number, P.O. box, c/o."
            value={getValue("streetAddress", fields)}
          />
        </SensitiveData>
      </PaymentFormField>
      <PaymentFormField id="extendedAddress" label="Street address 2" span={4}>
        <SensitiveData>
          <TextInput
            // @ts-ignore - This component is housed in the packages/products package, so the missing `hasError` type will have to be fixed there
            hasError={hasError("extendedAddress", fields)}
            onChange={handleChange(onChangeField, "extendedAddress")}
            placeholder="Apartment, suite, unit, building, floor, etc."
            value={getValue("extendedAddress", fields)}
          />
        </SensitiveData>
      </PaymentFormField>
      <PaymentFormField
        id="locality"
        label="City"
        required={true}
        span={isSmall ? 4 : 2}
      >
        <TextInput
          // @ts-ignore - This component is housed in the packages/products package, so the missing `hasError` type will have to be fixed there
          hasError={hasError("locality", fields)}
          onChange={handleChange(onChangeField, "locality")}
          placeholder="City"
          value={getValue("locality", fields)}
        />
      </PaymentFormField>
      <PaymentFormField
        id="region"
        label="State"
        required={true}
        span={isSmall ? 4 : 1}
      >
        <StateSelect
          hasError={hasError("region", fields)}
          onChange={handleChange(onChangeField, "region")}
          value={getValue("region", fields)}
        />
      </PaymentFormField>
      <PaymentFormField
        id="postalCode"
        label="Zip"
        required={true}
        span={isSmall ? 4 : 1}
      >
        <PostalCodeInput
          hasError={hasError("postalCode", fields)}
          onChange={handleChange(onChangeField, "postalCode")}
          value={getValue("postalCode", fields)}
        />
      </PaymentFormField>
    </div>
  );
};

export default BankAccountFormFields;
